import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import {
    enableProdMode,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideZonelessChangeDetection,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { DashboardEffects } from '@app/+state/dashboard.effects';
import { dashboardFeature } from '@app/+state/dashboard.feature';
import appRoutes from '@app/app-routes';
import { AuthInterceptor } from '@app/auth/interceptors/auth-interceptors';
import { PaymentMethodPipe } from '@app/core/pipes/payment-method.pipe';
import { TranslocoMatPaginatorIntl } from '@app/dashboard/providers/transloco-mat-paginator-intl.provider';
import { TranslocoSrcLoader } from '@app/transloco/transloco-loader';
import { LanguageService, provideLanguageService } from '@cirklo/angular/i18n';
import { provideApi } from '@cirklo/client';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { Amplify } from 'aws-amplify';
import {
    BarController,
    BarElement,
    CategoryScale,
    Colors,
    Filler,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
} from 'chart.js';
import { provideCharts } from 'ng2-charts';
import { NgxCurrencyConfig, NgxCurrencyInputMode, provideEnvironmentNgxCurrency } from 'ngx-currency';
import { provideLottieOptions } from 'ngx-lottie';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

if (environment.production) {
    enableProdMode();
}

registerLocaleData(localeFr);
registerLocaleData(localeNl);
registerLocaleData(localeEn);

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: environment.awsConfig.aws_user_pools_id,
            userPoolClientId: environment.awsConfig.aws_user_pools_web_client_id,
        },
    },
});

export const customCurrencyMaskConfig: NgxCurrencyConfig = {
    align: 'left',
    allowNegative: true,
    allowZero: true,
    decimal: ',',
    precision: 2,
    prefix: '',
    suffix: '',
    thousands: '.',
    nullable: true,
    inputMode: NgxCurrencyInputMode.Natural,
};

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(
            appRoutes,
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withComponentInputBinding(),
        ),
        { provide: LOCALE_ID, useValue: 'nl-BE' },
        provideEnvironmentNgxCurrency(customCurrencyMaskConfig),
        TranslocoCurrencyPipe,
        provideNativeDateAdapter(),
        provideLottieOptions({ player: () => import('lottie-web/build/player/lottie_light') }),
        provideCharts({
            registerables: [
                LinearScale,
                LineController,
                CategoryScale,
                BarElement,
                BarController,
                LineElement,
                PointElement,
                Tooltip,
                Colors,
                Filler,
            ],
        }),
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: MatPaginatorIntl, useClass: TranslocoMatPaginatorIntl },
        provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
        provideStore({ [dashboardFeature.name]: dashboardFeature.reducer }),
        provideEffects(DashboardEffects),
        provideLanguageService({
            loader: TranslocoSrcLoader,
            selector: dashboardFeature.selectLanguage,
        }),
        PaymentMethodPipe,
        provideApi({ basePath: environment.apiURL }),
        provideAppInitializer(() => inject(LanguageService).initialize()),
    ],
}).catch((err) => console.error(err));
